import { SHARE_INTENTS, ShareIntentError } from '@/utils/shareIntents';
import { log as debugLog, error as debugError } from '@/utils/debugLogger';

/**
 * Vuex module for the share-intent bus. Receives "play this intent" requests
 * from URL parsers and dispatches them through the registered intent
 * handlers. Concentrates error UX (toast on ShareIntentError kinds) and
 * provides a `playing` flag so MainView's URL-handler guard plus our own
 * playing-flag together prevent double execution.
 *
 * Registration: src/store/index.js as 'SHARE_INTENT'.
 */
export const SHARE_INTENT = {
  namespaced: true,
  state: {
    current: null,
    playing: false,
  },
  mutations: {
    setCurrent(state, intent) {
      state.current = intent;
    },
    setPlaying(state, playing) {
      state.playing = playing;
    },
    clear(state) {
      state.current = null;
      state.playing = false;
    },
  },
  actions: {
    /**
     * Play a decoded intent. Looks up the handler in the registry by
     * intent.type and invokes its play() with full Vuex context.
     *
     * Errors of kind ShareIntentError surface as user-facing toasts; other
     * errors are logged and swallowed (intent is cleared regardless so the
     * UI doesn't appear stuck).
     */
    async play({ commit, dispatch, state, rootState }, intent) {
      if (state.playing) {
        debugLog('[SHARE_INTENT/play] already playing, skipping');
        return;
      }
      if (!intent?.type) {
        debugError('[SHARE_INTENT/play] invalid intent (no type)', intent);
        return;
      }
      const handler = SHARE_INTENTS[intent.type];
      if (!handler) {
        debugError(`[SHARE_INTENT/play] no handler for type=${intent.type}`);
        return;
      }

      debugLog(`[SHARE_INTENT/play] dispatching intent type=${intent.type}`);
      commit('setCurrent', intent);
      commit('setPlaying', true);

      try {
        // Second layer behind the router's facility guard (defense in depth).
        // Normally free: CHECK_ACCESS hits the cache the guard just warmed.
        const allowed = await dispatch('FACILITY/CHECK_ACCESS', rootState.assetId, { root: true });
        if (!allowed) {
          throw new ShareIntentError('No access to this facility', 'facility-access-denied');
        }

        await handler.play(intent, { dispatch, commit, state, rootState });
        debugLog(`[SHARE_INTENT/play] completed type=${intent.type}`);
      } catch (err) {
        if (err instanceof ShareIntentError) {
          debugError(`[SHARE_INTENT/play] ShareIntentError kind=${err.kind} message=${err.message}`);
          // UX-tier toast hook — kind-specific friendly message.
          // The `mainApp` ref carries $dialog from the root component (set in
          // MainView.mounted via `this.$store.state.mainApp = this`). Falls
          // through silently if unavailable (e.g., during boot before MainView
          // mounts), which is acceptable — the URL handler is the only caller
          // and runs after MainView mount.
          const dialog = rootState.mainApp?.$dialog;
          if (dialog?.notify) {
            const friendlyMessages = {
              'forge-not-ready': 'Viewer is not ready yet — try again in a moment',
              'task-not-found': 'Task not found',
              'task-has-no-tags': 'Task has no associated tags to view',
              'tags-unavailable': 'Could not load task tags',
              'permission-denied': 'You do not have permission to view this task',
              'facility-mismatch': 'This link points to a task in a different facility',
              'facility-access-denied': 'You do not have access to this facility',
            };
            dialog.notify.error?.(friendlyMessages[err.kind] ?? err.message);
          }
          if (err.kind === 'facility-access-denied') {
            // Get the user off the facility they can't access. `.catch` swallows
            // NavigationDuplicated when the router guard already redirected.
            const router = rootState.mainApp?.$router;
            if (router) {
              router
                .replace({
                  name: 'error-not-authorize',
                  query: { redirected: rootState.mainApp?.$route?.fullPath },
                })
                .catch(() => {});
            }
          }
        } else {
          debugError('[SHARE_INTENT/play] unexpected error', err);
        }
      } finally {
        commit('clear');
      }
    },
  },
};
